import { useEffect, useRef, useState } from "react";

import { useDebounce, useMenu } from "hooks";

import { MultiSelectResults, SearchWrapper } from "../shared";
import { BEMultiProps } from "../shared/types";

export default <T,>({
  id,
  itemKey,
  selectedItems,
  items,
  page,
  pages,
  setPage,
  onSearch,
  renderListItemLabel,
  onSelect,
  buttonLabel,
  label,
  placeholder,
  hint,
  listHeader,
  footer,
  emptyState,
  isLoading,
  errorMessage,
  fixedHeight = "h-96",
  showPagination = true,
  showSelectAll = true,
}: Omit<BEMultiProps<T>, "isMultiSelect">): React.JSX.Element => {
  const [currentSelection, setCurrentSelection] = useState<T[]>(selectedItems);
  const [searchInput, setSearchInput] = useState("");
  const debouncedValue = useDebounce(searchInput, 750);

  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useMenu(menuRef);
  const toggleIsOpen = () => setIsOpen(!isOpen);

  useEffect(() => {
    setCurrentSelection(selectedItems);
  }, [selectedItems.length]);

  useEffect(() => {
    onSearch(searchInput);
  }, [debouncedValue]);

  return (
    <SearchWrapper
      menuRef={menuRef}
      id={id}
      defaultSearchInput={searchInput}
      onSearch={setSearchInput}
      isOpen={isOpen}
      toggleIsOpen={toggleIsOpen}
      buttonLabel={
        typeof buttonLabel === "function" ? buttonLabel(isOpen) : buttonLabel
      }
      label={label}
      placeholder={placeholder}
      hint={hint}
      isLoading={isLoading}
      errorMessage={errorMessage}
      fixedHeight={fixedHeight}
    >
      <MultiSelectResults<T>
        itemKey={itemKey}
        items={items}
        selection={currentSelection}
        setSelection={setCurrentSelection}
        listHeader={listHeader}
        renderListItemLabel={renderListItemLabel}
        onSelect={onSelect}
        page={page}
        pages={pages}
        setPage={setPage}
        emptyState={emptyState}
        footer={footer}
        showPagination={showPagination}
        showSelectAll={showSelectAll}
      />
    </SearchWrapper>
  );
};
